import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { CopyableId } from "../components/CopyableId.js";
import { useAdminMe } from "../hooks/useAdminMe.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { switchActiveWorkspace } from "../lib/switchWorkspace.js";
import { workspaceRoleBadgeVariant } from "../lib/workspaceRolePresentation.js";
import type { WorkspaceRole } from "../hooks/useAdminMe.js";

export const SettingsAccountPage = () => {
  const queryClient = useQueryClient();
  const me = useAdminMe();
  const { workspaceId, isLoading } = useWorkspaceId();

  if (isLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  const user = me.data?.user ?? null;
  const workspaces = me.data?.workspaces ?? [];
  const activeId = me.data?.activeWorkspaceId ?? null;

  if (user === null) {
    return <p className="text-sm text-destructive">Could not load account.</p>;
  }

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Account"
        description="Your sign-in identity, support identifiers, and workspaces you can access."
      />

      <section className="admin-panel" aria-labelledby="account-signin-heading">
        <div className="admin-panel-header">
          <h2 id="account-signin-heading" className="admin-panel-title">
            Sign-in
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <dl className="admin-settings-dl">
            <dt>Email</dt>
            <dd>{user.email}</dd>
          </dl>
          <div style={{ marginTop: "1.25rem" }}>
            <CopyableId
              id={user.id}
              label="User ID"
              helper="Include this when contacting support."
              buttonLabel="Copy ID"
            />
          </div>
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="account-session-heading">
        <div className="admin-panel-header">
          <h2 id="account-session-heading" className="admin-panel-title">
            Session
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <p className="admin-settings-row-desc" style={{ margin: 0 }}>
            The active workspace is stored in this browser tab (session storage) so refreshes keep
            context. Use the header menu or the table below to switch workspaces.
          </p>
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="account-workspaces-heading">
        <div className="admin-panel-header">
          <h2 id="account-workspaces-heading" className="admin-panel-title">
            Your workspaces
          </h2>
        </div>
        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Your role</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspaces.map((w) => {
                const isActive = w.id === activeId;
                return (
                  <TableRow key={w.id}>
                    <TableCell>
                      <p className="text-sm font-medium">{w.name}</p>
                      {isActive ? (
                        <p className="text-xs text-muted-foreground">Active in this tab</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <span className="admin-mono-inline">{w.slug}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={workspaceRoleBadgeVariant(w.role as WorkspaceRole)}>
                        {w.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant={isActive ? "secondary" : "outline"}
                          disabled={isActive}
                          onClick={() => {
                            switchActiveWorkspace(queryClient, w.id);
                          }}
                        >
                          {isActive ? "Current" : "Switch here"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
};
